/**
 * Character names on WoW Forever.
 *
 * Forever has no realms, and every character has a first and a family name -
 * "Lewolff Fouras" rather than "Lewolff". Every era before it gave us a single
 * word, and the whole site compares names by lowercasing two strings and
 * testing them for equality. That is fine for one word and fragile for two:
 * a stray double space, a non-breaking space pasted out of Discord, or someone
 * typing only the first name all produce a miss that looks like a missing
 * player.
 *
 * The full name stays the identity - one `character_name` column, exactly as
 * the game reports it. Splitting it into two columns would invite the halves
 * and the canonical string to disagree, and nothing in the app needs to query
 * on a family name. What is needed is comparing and displaying it carefully,
 * which is what this file is for.
 */

/** Collapse whitespace and normalise unicode, keeping the name as typed. */
export function normalizeName(name: string): string {
  return name
    .normalize('NFC')
    .replace(/[\s ]+/g, ' ')
    .trim()
}

/** The form to compare or key a map by. Never display this. */
export function nameKey(name: string): string {
  return normalizeName(name).toLowerCase()
}

export function sameCharacter(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  return nameKey(a) === nameKey(b)
}

/** "Lewolff Fouras" -> "Lewolff". A single-word name is returned unchanged. */
export function firstName(name: string): string {
  return normalizeName(name).split(' ')[0] ?? ''
}

/** "Lewolff Fouras" -> "Fouras", or '' when there is no family name. */
export function familyName(name: string): string {
  const parts = normalizeName(name).split(' ')
  return parts.length > 1 ? parts.slice(1).join(' ') : ''
}

/**
 * The short form for places where a full name will not fit - a raid group
 * slot, a compact table column.
 *
 * Only safe where the surrounding list makes it unambiguous, which is why it
 * takes the other names on screen: two people called Lewolff in the same raid
 * must not both render as "Lewolff". When the first name is shared, the full
 * name is returned instead.
 */
export function shortName(name: string, others: readonly string[] = []): string {
  const first = firstName(name)
  const clashes = others.some(
    other => !sameCharacter(other, name) && nameKey(firstName(other)) === nameKey(first),
  )
  return clashes ? normalizeName(name) : first
}

/**
 * Find a character by name, the way an import should.
 *
 * Signups arrive from Discord, where people go by their first name alone, so
 * a first name is the normal case rather than the exception - asking someone
 * to confirm 25 of them every raid would be useless. A first name that
 * belongs to exactly one character on the roster is therefore an answer.
 *
 * What is never an answer is a first name shared by two characters. That is
 * unlikely in a guild this size, but it is the one case where guessing
 * assigns somebody else's attendance and loot, so it is returned as
 * `ambiguous` for a human to settle instead.
 */
export function matchCharacter<T>(
  needle: string,
  candidates: readonly T[],
  nameOf: (candidate: T) => string,
): { match: T | null; ambiguous: T[] } {
  const key = nameKey(needle)

  // A full name always wins, even if somebody shares its first name
  const exact = candidates.find(c => nameKey(nameOf(c)) === key)
  if (exact) return { match: exact, ambiguous: [] }

  const byFirstName = candidates.filter(c => nameKey(firstName(nameOf(c))) === nameKey(firstName(needle)))
  if (byFirstName.length === 1) return { match: byFirstName[0], ambiguous: [] }
  if (byFirstName.length > 1) return { match: null, ambiguous: byFirstName }

  return { match: null, ambiguous: [] }
}
